import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../../../../API';

function itemMatchesFilter(item, filter) {
    if (!filter) return true;
    return String(item?.name || '').toLowerCase().includes(filter.toLowerCase());
}

/**
 * Browse and select staged items. Default is a standalone modal; `embedded`
 * renders the tree for use inside AddSlidesDialog (shared footer).
 */
export default function StagedItemsBrowser({
    isOpen,
    onClose,
    onAccept,
    acceptLoading = false,
    embedded = false,
    hideFooter = false,
    onSelectionChange,
}) {
    const [expandedFolders, setExpandedFolders] = useState(new Set());
    const [folderContents, setFolderContents] = useState({}); // { folderId: { folders: [], items: [] } }
    const [selectedItems, setSelectedItems] = useState(new Set());
    const [selectedFolders, setSelectedFolders] = useState(new Set()); // { folderId } - track selected folders
    const [allItemsMap, setAllItemsMap] = useState({}); // { itemId: item } - track all items for selection
    const [nameFilter, setNameFilter] = useState('');

    // Fetch staging folder root
    const { data: stagingFolderResponse, refetch: refetchStagingFolder } = useQuery({
        queryKey: ['network-staging-folder'],
        queryFn: () => api.get('/network/staging/folder'),
        enabled: isOpen,
        staleTime: 5 * 60 * 1000,
    });

    // Extract staging folder data
    const stagingFolderData = stagingFolderResponse?.status === 200 ? stagingFolderResponse.data : null;
    const rootStagingFolder = stagingFolderData?.folder || null;
    const rootStagingFolders = stagingFolderData?.folders || [];
    const rootStagingItems = stagingFolderData?.items || [];

    // Initialize root folder contents when staging folder loads
    useEffect(() => {
        if (rootStagingFolder && !folderContents[rootStagingFolder._id]) {
            setFolderContents(prev => ({
                ...prev,
                [rootStagingFolder._id]: {
                    folders: rootStagingFolders,
                    items: rootStagingItems
                }
            }));
            
            // Add root items to allItemsMap
            const itemsMap = {};
            rootStagingItems.forEach(item => {
                itemsMap[item._id] = item;
            });
            setAllItemsMap(prev => ({ ...prev, ...itemsMap }));
        }
    }, [rootStagingFolder, rootStagingFolders, rootStagingItems]);

    // Toggle folder expansion
    const handleFolderToggle = useCallback(async (folderId) => {
        const isExpanded = expandedFolders.has(folderId);
        
        if (isExpanded) {
            // Collapse - remove from expanded set
            setExpandedFolders(prev => {
                const next = new Set(prev);
                next.delete(folderId);
                return next;
            });
        } else {
            // Expand - add to expanded set and fetch contents if not already loaded
            setExpandedFolders(prev => new Set(prev).add(folderId));
            
            // Fetch contents if not already loaded
            if (!folderContents[folderId]) {
                try {
                    const [foldersRes, itemsRes] = await Promise.all([
                        api.get('/folder', { query: { parentId: folderId, parentType: 'folder' } }),
                        api.get('/item', { query: { folderId: folderId } })
                    ]);
                    
                    const folders = foldersRes?.status === 200 ? (foldersRes.data || []) : [];
                    const items = itemsRes?.status === 200 ? (itemsRes.data || []) : [];
                    
                    setFolderContents(prev => ({
                        ...prev,
                        [folderId]: { folders, items }
                    }));
                    
                    // Add items to allItemsMap
                    const itemsMap = {};
                    items.forEach(item => {
                        itemsMap[item._id] = item;
                    });
                    setAllItemsMap(prev => ({ ...prev, ...itemsMap }));
                } catch (err) {
                    console.error(`Failed to load folder contents for ${folderId}:`, err);
                }
            }
        }
    }, [expandedFolders, folderContents]);

    const handleItemToggle = useCallback((itemId) => {
        setSelectedItems(prev => {
            const next = new Set(prev);
            const wasSelected = next.has(itemId);
            if (wasSelected) {
                next.delete(itemId);
            } else {
                next.add(itemId);
            }
            
            // Update folder selection state based on item selection
            setSelectedFolders(prevFolders => {
                const nextFolders = new Set(prevFolders);
                // Find which folder(s) contain this item
                Object.entries(folderContents).forEach(([folderId, contents]) => {
                    // Only consider folders with no child folders (selectable folders)
                    if (contents.folders.length > 0) {
                        return;
                    }
                    
                    const folderItems = contents.items || [];
                    const itemInFolder = folderItems.some(item => item._id === itemId);
                    
                    if (itemInFolder) {
                        // Check if all items in this folder are now selected
                        const allSelected = folderItems.length > 0 && folderItems.every(item => next.has(item._id));
                        
                        if (allSelected && !nextFolders.has(folderId)) {
                            // All items selected - check the folder
                            nextFolders.add(folderId);
                        } else if (!allSelected && nextFolders.has(folderId)) {
                            // Not all items selected - uncheck the folder
                            nextFolders.delete(folderId);
                        }
                    }
                });
                return nextFolders;
            });
            
            return next;
        });
    }, [folderContents]);

    const handleFolderSelectToggle = useCallback((folderId, e) => {
        e.stopPropagation(); // Prevent folder expansion when clicking checkbox
        const contents = folderContents[folderId] || { folders: [], items: [] };
        
        // Defensive check: only allow selection if folder has no child folders
        if (contents.folders.length > 0) {
            console.warn(`Cannot select folder ${folderId}: it contains child folders`);
            return;
        }

        setSelectedFolders(prev => {
            const next = new Set(prev);
            if (next.has(folderId)) {
                next.delete(folderId);
                // Also deselect all items in this folder
                if (contents.items) {
                    setSelectedItems(prevItems => {
                        const nextItems = new Set(prevItems);
                        contents.items.forEach(item => nextItems.delete(item._id));
                        return nextItems;
                    });
                }
            } else {
                next.add(folderId);
                // Also select all items in this folder
                if (contents.items) {
                    setSelectedItems(prevItems => {
                        const nextItems = new Set(prevItems);
                        contents.items.forEach(item => nextItems.add(item._id));
                        return nextItems;
                    });
                }
            }
            return next;
        });
    }, [folderContents]);

    const selectionPayload = useMemo(() => {
        const folderItemIds = new Set();
        const selectedFolderSelections = [];

        selectedFolders.forEach((folderId) => {
            const contents = folderContents[folderId] || { folders: [], items: [] };
            if (contents.folders.length > 0) return;
            const folderItems = contents.items || [];
            folderItems.forEach((item) => folderItemIds.add(item._id));
            selectedFolderSelections.push({ folderId, items: folderItems });
        });

        const selectedItemsArray = Array.from(selectedItems)
            .filter((itemId) => !folderItemIds.has(itemId))
            .map((itemId) => allItemsMap[itemId])
            .filter((item) => item !== undefined);

        return { items: selectedItemsArray, folders: selectedFolderSelections };
    }, [selectedItems, selectedFolders, folderContents, allItemsMap]);

    useEffect(() => {
        if (!onSelectionChange) return;
        onSelectionChange(selectionPayload);
    }, [onSelectionChange, selectionPayload]);

    const handleAccept = useCallback(async () => {
        if (selectionPayload.items.length === 0 && selectionPayload.folders.length === 0) {
            return;
        }
        try {
            await onAccept(selectionPayload);
        } catch (_) {
            // Error already shown in acceptAndSaveStagedItems
        }
    }, [selectionPayload, onAccept]);

    const handleRefresh = useCallback(async () => {
        // Refresh staging folder (which will refresh root contents)
        await refetchStagingFolder();
        
        // Refresh all expanded folders
        const refreshPromises = Array.from(expandedFolders).map(async (folderId) => {
            try {
                const [foldersRes, itemsRes] = await Promise.all([
                    api.get('/folder', { query: { parentId: folderId, parentType: 'folder' } }),
                    api.get('/item', { query: { folderId: folderId } })
                ]);
                
                const folders = foldersRes?.status === 200 ? (foldersRes.data || []) : [];
                const items = itemsRes?.status === 200 ? (itemsRes.data || []) : [];
                
                setFolderContents(prev => ({
                    ...prev,
                    [folderId]: { folders, items }
                }));
                
                // Update allItemsMap
                const itemsMap = {};
                items.forEach(item => {
                    itemsMap[item._id] = item;
                });
                setAllItemsMap(prev => ({ ...prev, ...itemsMap }));
            } catch (err) {
                console.error(`Failed to refresh folder ${folderId}:`, err);
            }
        });
        
        await Promise.all(refreshPromises);
    }, [refetchStagingFolder, expandedFolders]);

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setExpandedFolders(new Set());
            setFolderContents({});
            setSelectedItems(new Set());
            setSelectedFolders(new Set());
            setAllItemsMap({});
            setNameFilter('');
        }
    }, [isOpen]);

    // Recursive component for rendering folder tree
    const renderFolderTree = useCallback((folderList, level = 0) => {
        return folderList.map(folder => {
            const isExpanded = expandedFolders.has(folder._id);
            const contents = folderContents[folder._id] || { folders: [], items: [] };
            const isSelectableFolder = contents.folders.length === 0 && isExpanded; // Only show checkbox if expanded and has no child folders
            const isFolderSelected = selectedFolders.has(folder._id);
            
            return (
                <div key={folder._id} style={{ marginBottom: '0.25em' }}>
                    <div 
                        style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '0.5em',
                            padding: '0.5em',
                            marginLeft: `${level * 20}px`,
                            cursor: 'pointer',
                            borderRadius: '4px',
                            backgroundColor: isExpanded ? '#f0f0f0' : 'transparent'
                        }}
                        onClick={() => handleFolderToggle(folder._id)}
                    >
                        <span style={{ width: '20px', textAlign: 'center', fontSize: '0.8em' }}>
                            {isExpanded ? '▼' : '▶'}
                        </span>
                        {isSelectableFolder && (
                            <input
                                type="checkbox"
                                checked={isFolderSelected}
                                onChange={(e) => handleFolderSelectToggle(folder._id, e)}
                                onClick={(e) => e.stopPropagation()}
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                        )}
                        <span>📁</span>
                        <span style={{ flex: 1 }}>{folder.name}</span>
                    </div>
                    {isExpanded && (
                        <div>
                            {/* Render child folders recursively */}
                            {contents.folders.length > 0 && renderFolderTree(contents.folders, level + 1)}
                            {/* Render child items */}
                            {contents.items.filter((item) => itemMatchesFilter(item, nameFilter)).map(item => (
                                <label
                                    key={item._id}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.75em',
                                        padding: '0.5em',
                                        marginLeft: `${(level + 1) * 20}px`,
                                        cursor: 'pointer',
                                        borderRadius: '4px',
                                        backgroundColor: selectedItems.has(item._id) ? '#e3f2fd' : 'transparent'
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedItems.has(item._id)}
                                        onChange={() => handleItemToggle(item._id)}
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                    />
                                    <span>📄</span>
                                    <span style={{ flex: 1 }}>{item.name}</span>
                                    {item.size && (
                                        <span style={{ color: '#666', fontSize: '0.9em' }}>
                                            {(item.size / 1024 / 1024).toFixed(2)} MB
                                        </span>
                                    )}
                                </label>
                            ))}
                        </div>
                    )}
                </div>
            );
        });
    }, [expandedFolders, folderContents, selectedItems, selectedFolders, handleFolderToggle, handleItemToggle, handleFolderSelectToggle, nameFilter]);

    if (!isOpen) return null;

    const rootContents = rootStagingFolder ? (folderContents[rootStagingFolder._id] || { folders: rootStagingFolders, items: rootStagingItems }) : { folders: [], items: [] };
    const filteredRootItems = (rootContents.items || []).filter((item) => itemMatchesFilter(item, nameFilter));
    const showFooter = !hideFooter && !embedded;

    const tree = (
        <>
            {!embedded && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1.5em',
                    borderBottom: '1px solid #ddd',
                    paddingBottom: '0.5em'
                }}>
                    <h3 style={{ margin: 0 }}>Select Staged Items</h3>
                    <div style={{ display: 'flex', gap: '0.5em', alignItems: 'center' }}>
                        <button
                            onClick={handleRefresh}
                            style={{
                                padding: '0.25em 0.75em',
                                backgroundColor: '#2196f3',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.9em'
                            }}
                            title="Refresh"
                        >
                            ↻ Refresh
                        </button>
                        <button
                            onClick={onClose}
                            style={{
                                background: 'none',
                                border: 'none',
                                fontSize: '1.5em',
                                cursor: 'pointer',
                                color: '#666',
                                padding: '0 0.5em'
                            }}
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}

            <div style={{ display: 'flex', gap: '0.5em', marginBottom: '1em', alignItems: 'center' }}>
                <input
                    type="search"
                    value={nameFilter}
                    onChange={(e) => setNameFilter(e.target.value)}
                    placeholder="Filter by filename"
                    aria-label="Filter staging items by name"
                    style={{
                        flex: 1,
                        padding: '0.45em 0.65em',
                        border: '1px solid #d0d7de',
                        borderRadius: '6px',
                        fontSize: '0.9em'
                    }}
                />
                <button
                    type="button"
                    onClick={handleRefresh}
                    style={{
                        padding: '0.45em 0.75em',
                        backgroundColor: '#2196f3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.9em'
                    }}
                >
                    Refresh
                </button>
            </div>

            {rootStagingFolder && (
                <div style={{ marginBottom: '1em', fontWeight: 'bold', color: '#666' }}>
                    {rootStagingFolder.name}
                </div>
            )}

            {filteredRootItems.length > 0 && (
                <div style={{ marginBottom: '1.5em' }}>
                    <h4 style={{ marginBottom: '0.5em' }}>Items</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5em' }}>
                        {filteredRootItems.map((item) => (
                            <label
                                key={item._id}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75em',
                                    padding: '0.75em',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    backgroundColor: selectedItems.has(item._id) ? '#e3f2fd' : '#fff',
                                    cursor: 'pointer'
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedItems.has(item._id)}
                                    onChange={() => handleItemToggle(item._id)}
                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                <span>📄</span>
                                <span style={{ flex: 1 }}>{item.name}</span>
                                {item.size && (
                                    <span style={{ color: '#666', fontSize: '0.9em' }}>
                                        {(item.size / 1024 / 1024).toFixed(2)} MB
                                    </span>
                                )}
                            </label>
                        ))}
                    </div>
                </div>
            )}

            {rootContents.folders.length > 0 && (
                <div style={{ marginBottom: '1.5em' }}>
                    <h4 style={{ marginBottom: '0.5em' }}>Folders</h4>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {renderFolderTree(rootContents.folders, 0)}
                    </div>
                </div>
            )}

            {rootContents.folders.length === 0 && filteredRootItems.length === 0 && (
                <p style={{ color: '#666', fontStyle: 'italic' }}>
                    {nameFilter ? 'No staging items match that name' : 'No folders or items in staging area'}
                </p>
            )}

            {showFooter && (
                <div style={{ display: 'flex', gap: '1em', justifyContent: 'flex-end', marginTop: '1.5em' }}>
                    <button
                        onClick={onClose}
                        style={{
                            padding: '0.5em 1em',
                            backgroundColor: '#ccc',
                            color: 'black',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleAccept}
                        disabled={(selectedItems.size === 0 && selectedFolders.size === 0) || acceptLoading}
                        style={{
                            padding: '0.5em 1em',
                            backgroundColor: (selectedItems.size === 0 && selectedFolders.size === 0) || acceptLoading ? '#ccc' : '#2196f3',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: (selectedItems.size === 0 && selectedFolders.size === 0) || acceptLoading ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {acceptLoading ? 'Adding to case…' : `Accept (${selectedItems.size}${selectedFolders.size > 0 ? ` + ${selectedFolders.size} folder${selectedFolders.size > 1 ? 's' : ''}` : ''})`}
                    </button>
                </div>
            )}
        </>
    );

    if (embedded) {
        return <div>{tree}</div>;
    }

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
            }}
            onClick={onClose}
        >
            <div
                style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '2em',
                    maxWidth: '800px',
                    width: '90%',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {tree}
            </div>
        </div>
    );
}
